import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react'
import { use } from 'react'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'

import { cn } from '@gto/ui/cn'

import { tabVariants } from './tabs.styles'
import { TabsContext } from './tabs-context'

type BaseTabProps = Omit<ComponentPropsWithoutRef<typeof BaseTabs.Tab>, 'value' | 'children'>

export type TabProps = BaseTabProps & {
  /** Tab value — selects the matching Panel */
  value: string | number
  /** Tab label content */
  children: ReactNode
  disabled?: boolean
  className?: string
  ref?: Ref<HTMLButtonElement>
  'data-tst'?: string
}

/**
 * Individual tab trigger. Selected and disabled state are driven by Base UI's
 * `aria-selected` and `aria-disabled` attributes on the rendered button —
 * Tailwind selectors in `tabs.styles.ts` pick them up so the React tree never
 * has to recompute class lists. Orientation is consumed from `TabsContext`
 * (not from a DOM attribute) and applied via the `orientation` tv() variant.
 */
export const Tab = ({
  value,
  children,
  disabled = false,
  className,
  ref,
  'data-tst': dataTestId,
  ...rest
}: TabProps) => {
  const { color, size, fullWidth, scrollable, detached, orientation } = use(TabsContext)

  return (
    <BaseTabs.Tab
      ref={ref}
      data-tst={dataTestId}
      data-ui="tab"
      disabled={disabled}
      value={value}
      className={cn(
        tabVariants({
          orientation,
          color,
          size,
          fullWidth,
          scrollable: scrollable !== false,
          detached,
        }),
        className,
      )}
      {...rest}
    >
      {children}
    </BaseTabs.Tab>
  )
}
